/**
 * React component for the showing embedded boards within the landing page
 *
 * Copyright (c) 2022 Propellant Software Corp
 * Author: Kelvin Nguyen
 */
import React, { useState, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import BrowserFrame from '../BrowserFrame'
import SectionHeader from '../SectionHeader'
import { DESTINATIONS } from './enums'
import DestinationCard from 'client/shared/js/components/DestinationCard'
import useMediaQuery from 'client/shared/js/hooks/useMediaQuery'
import { trackEvent } from 'client/shared/js/utils/tracking'
import styles from './DestinationsEmbed.module.scss'

const DestinationsEmbed = ({ className }) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [renderedIndices, setRenderedIndices] = useState({ 0: true }) // used to optimize iframe rendering
  const { isTablet, isMobile } = useMediaQuery()

  const onDestinationClick = useCallback((index) => {
    trackEvent('Selecting Popular Destination', {
      'Destination': DESTINATIONS[index].destination
    })
    setSelectedIndex(index)
    setRenderedIndices({
      ...renderedIndices,
      [index]: true
    })
  }, [renderedIndices])

  // If the frame has been rendered before, it will be updated within renderedIndices and keep it on
  //  the DOM (but hidden) so we don't have to keep reloading it when we cycle through them
  const EmbededBoards = useMemo(() => (
    DESTINATIONS.map(({ destination }, index) => {
      const embedUrl = DESTINATIONS[index].embedUrl
      const url = (isTablet || isMobile)
        ? embedUrl.mobile || embedUrl.browser
        : embedUrl.browser

      return (renderedIndices[index]) ? (
        <BrowserFrame
          key={`destination-frame-${destination}`}
          src={url}
          enableMobile={isTablet || isMobile}
          hidden={selectedIndex !== index} />
      ) : null
    }).filter((item) => !!item)
  ), [renderedIndices, selectedIndex, isTablet, isMobile])

  const DestinationCards = useMemo(() => (
    DESTINATIONS.map(({ destination, ...destinationProps }, index) => (
      <div
        key={`destination-card-${destination}`}
        className={cx(styles['destination-card-container'], {
          [styles['selected']]: index === selectedIndex
        })}
        onClick={() => onDestinationClick(index)}>
        <DestinationCard
          cardClass={cx(styles['destination-card'])}
          destination={destination}
          {...destinationProps} />
      </div>
    ))
  ), [selectedIndex, onDestinationClick])

  return (
    <section className={cx(className)}>
      <SectionHeader
        title='Popular trip plans'
        description='Check out some of our most popular boards from the most traveled cities among our users.' />
      <div className={cx(styles['destinations-embed-container'])}>
        <div className={cx(styles['destinations-container'])}>
          {DestinationCards}
        </div>
        <div className={cx(styles['board-embedder'])}>
          {EmbededBoards}
        </div>
      </div>
    </section>
  )
}

DestinationsEmbed.propTypes = {
  className: PropTypes.string
}

export default DestinationsEmbed
